import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform, ActivityIndicator, ScrollView } from 'react-native';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { usePersistentWidget } from '../context/PersistentWidgetContext';
import AnimatedCard from './AnimatedCard';
import { persistentWidgetService } from '../services/persistentWidgetService';

/**
 * Persistent Widget Indicator Card
 * 
 * Displays on dashboard showing available persistent widgets.
 * Mimics V12DiagnosticCard design but uses palette.primary color.
 */
const PersistentWidgetIndicator = ({ style }) => {
  const { palette } = useTheme();
  const { user } = useAuth();
  const { availableWidgets, openPersistentWidget, openReviewMode, loading: contextLoading, widgetStatuses } = usePersistentWidget();
  const [widgetCompletions, setWidgetCompletions] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (availableWidgets.length > 0) {
      loadCompletions();
    } else {
      setLoading(false);
    }
  }, [availableWidgets, user?.uid, widgetStatuses]);

  const loadCompletions = async () => {
    setLoading(true);
    try {
      const completions = {};
      for (const widget of availableWidgets) {
        if (!user?.uid) continue;
        const periodKey = persistentWidgetService.getPeriodKey(
          widget.schedule?.type || 'weekly',
          new Date(),
          widget.schedule
        );
        const { completion } = await persistentWidgetService.getLatestCompletionInPeriod(
          widget.id,
          user.uid,
          periodKey
        );
        completions[widget.id] = completion;
      }
      setWidgetCompletions(completions);
    } catch (error) {
      console.error('[PersistentWidgetIndicator] Error loading completions:', error);
    }
    setLoading(false);
  };

  const getScheduleDisplay = (schedule) => {
    if (!schedule || !schedule.type) return 'Not scheduled';
    
    switch (schedule.type) {
      case 'daily':
        return 'Daily';
      case 'weekly':
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return schedule.dayOfWeek !== undefined 
          ? `Weekly • ${dayNames[schedule.dayOfWeek]}`
          : 'Weekly';
      case 'monthly':
        return schedule.dayOfMonth !== undefined
          ? `Monthly • Day ${schedule.dayOfMonth}`
          : 'Monthly';
      default:
        return schedule.type;
    }
  };

  const getWidgetIcon = (widgetType) => {
    switch (widgetType) {
      case 'StrategicDecisionMatrix':
        return '🎯';
      case 'StrategicActionMatrix':
        return '⚡';
      case 'TigerTeamBreakoutsTracker':
        return '🐅';
      default:
        return '📊';
    }
  };

  if (loading || contextLoading) {
    return (
      <AnimatedCard 
        style={[
          styles.card, 
          { backgroundColor: palette.surface, borderColor: palette.primary || '#FF0600' },
          style
        ]}
      >
        <ActivityIndicator size="small" color={palette.primary || '#FF0600'} />
      </AnimatedCard>
    );
  }

  if (availableWidgets.length === 0) {
    return null; // Don't show if no widgets available
  }

  // Show all widgets (completed and non-completed)
  const widgetsToShow = availableWidgets.slice(0, 5); // Show up to 5 widgets
  const completedCount = availableWidgets.filter(w => widgetCompletions[w.id]).length;
  const dueCount = availableWidgets.length - completedCount;

  return (
    <AnimatedCard
      style={[
        styles.card,
        style
      ]}
    >
          <View style={styles.cardHeader}>
        <View style={styles.headerLeft}>
          <View>
            <Text style={styles.cardTitle}>Weekly Actions</Text>
            <Text style={styles.cardSubtitle}>
              {dueCount > 0 ? `${dueCount} due` : 'All complete'} • {completedCount} {completedCount === 1 ? 'completed' : 'completed'}
            </Text>
          </View>
        </View>
      </View>

      {/* Widget List */}
      <ScrollView style={styles.widgetList} showsVerticalScrollIndicator={false}>
        {widgetsToShow.map((widget, index) => {
          // Check both local completions and context statuses
          const completion = widgetCompletions[widget.id];
          const widgetStatus = widgetStatuses[widget.id];
          const isCompleted = !!completion || widgetStatus?.completed;
          const isDue = !isCompleted;

          const handlePress = () => {
            console.log('handlePress', widget.id, isCompleted);
            if (isCompleted) {
              // Open in review mode for the completed period
              openReviewMode(widget.id);
            } else {
              // Open normally for completion
              openPersistentWidget(widget.id);
            }
          };

          return (
            <TouchableOpacity
              key={widget.id}
              style={[
                styles.widgetItem,
                {
                  backgroundColor: isCompleted
                    ? 'rgba(34, 197, 94, 0.1)' // Green tint for completed
                    : isDue 
                      ? (palette.primary || '#FF0600') + '10' 
                      : 'rgba(255,255,255,0.05)',
                  borderColor: isCompleted
                    ? '#22C55E' // Green border for completed
                    : isDue 
                      ? (palette.primary || '#FF0600') 
                      : palette.border || 'rgba(255,255,255,0.1)',
                  marginBottom: index < widgetsToShow.length - 1 ? 12 : 0,
                }
              ]}
              onPress={handlePress}
            >
              <View style={styles.widgetItemHeader}>
                <Text style={[styles.widgetIcon, { color: isCompleted ? '#22C55E' : (palette.primary || '#FF0600') }]}>
                  {getWidgetIcon(widget.type)}
                </Text>
                <View style={styles.widgetItemInfo}>
                  <Text style={styles.widgetItemTitle} numberOfLines={1}>
                    {widget.title || 'Persistent Widget'}
                  </Text>
                  <Text style={styles.widgetItemSubtitle}>
                    {isCompleted ? 'Tap to review' : getScheduleDisplay(widget.schedule)}
                  </Text>
                </View>
                {isCompleted && (
                  <View style={[styles.completedBadge, { backgroundColor: '#22C55E' }]}>
                    <Text style={styles.completedBadgeText}>✓</Text>
                  </View>
                )}
              </View>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

    </AnimatedCard>
  );
};

const styles = StyleSheet.create({
  card: {
    padding: 20,
    borderRadius: 16,
    borderWidth: 3,
    borderColor: '#000000',
    backgroundColor: 'white',
    gap: 16,
    ...Platform.select({
      web: {
        boxShadow: '5px 6px 0px rgba(0,0,0,0.85)',
        overflow: 'visible',
      },
    }),
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  badge: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 4,
  },
  badgeText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '900',
    letterSpacing: 2,
  },
  cardTitle: {
    fontSize: 18,
    fontFamily: 'Chivo',
    fontWeight: '700',
    color: '#000000',
  },
  cardSubtitle: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '500',
    marginTop: 2,
    color: 'rgba(0,0,0,0.6)',
  },
  widgetList: {
    // No maxHeight - let it expand to fit all widgets
    // Add padding to accommodate box shadows on inner items
    paddingRight: 6,
    paddingBottom: 6,
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  widgetItem: {
    padding: 12,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#000000',
    backgroundColor: 'rgba(79, 205, 196, 0.15)',
    ...Platform.select({
      web: {
        boxShadow: '2px 3px 0px rgba(0,0,0,0.8)',
      },
    }),
  },
  widgetItemHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  widgetIcon: {
    fontSize: 24,
  },
  widgetItemInfo: {
    flex: 1,
  },
  widgetItemTitle: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: '#000000',
  },
  widgetItemSubtitle: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '500',
    marginTop: 2,
    color: 'rgba(0,0,0,0.6)',
  },
  completedBadge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: '#000000',
  },
  completedBadgeText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '700',
  },
  actionButton: {
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 10,
    borderWidth: 3,
    borderColor: '#000000',
    alignItems: 'center',
    backgroundColor: '#FF4747',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        boxShadow: '3px 4px 0px rgba(0,0,0,0.85)',
        transition: 'all 0.2s ease',
      },
    }),
  },
  actionButtonText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
});

export default PersistentWidgetIndicator;

